import { useRef, useState } from "react";
import type { FormInstance } from "antd";
import { axiosInstance } from "@/utilities";
import { API_URL_USERS } from "@/services/XCONS_ENV";

// Mirrors the backend's `normalizeWhatsappNumber` (users.service.ts) so the
// frontend can compare what the operator typed against what /users/lookup
// returns. Keep in sync if backend rules change.
export const normalizeWhatsapp = (input: string | null | undefined): string => {
  let n = String(input ?? "").replace(/\D/g, "");
  n = n.replace(/^5415/, "5411");
  n = n.replace(/^549/, "54");
  return n;
};

export const normalizeEmail = (input: string | null | undefined): string =>
  String(input ?? "").trim().toLowerCase();

/** Lo que devuelve `/users/lookup` por campo. */
export type ContactLookupMatch = {
  userId: number;
  email: string;
  whatsapp: string | null;
  firstname: string;
  lastname: string;
  isContactInStore: boolean;
  /**
   * Si el operador puede **ver** esa ficha (xcons-docs#549). Sólo viene con `isContactInStore`; en
   * `false` el match llega sin nombre, teléfono ni `contactId`: el alta avisa que la persona ya es
   * contacto del store y no ofrece abrirla.
   */
  canViewContact?: boolean;
  /** Ficha de esa identidad EN ESTE store, si tiene (y el operador puede verla). */
  contactId: number | null;
};

export type ContactConflictResult = {
  whatsappError: string | null;
  emailError: string | null;
  /**
   * Los matches crudos, para que el formulario pueda **ofrecer una salida** en vez de sólo pintar el
   * error: abrir la ficha que ya existe en este store, o unificar cuando el e-mail y el teléfono son
   * de dos contactos distintos.
   */
  byWhatsapp: ContactLookupMatch | null;
  byEmail: ContactLookupMatch | null;
};

/**
 * Consulta `/users/lookup` y bloquea **un solo caso**: que la persona ya tenga ficha en este store
 * (por mail o por teléfono). Ahí no hay nada que crear y el error ofrece abrir la que existe.
 *
 * Todo lo demás procede (D8): que la identidad exista en otro store es un reuso válido, y que uno
 * de los dos datos matchee y el otro no también — el backend reusa la identidad, descarta el dato
 * que no corresponde y devuelve al guardar qué campos quedaron con el valor del ecosistema.
 *
 * Devuelve además los matches crudos, que el formulario usa para ofrecer una salida (abrir la ficha
 * existente, o unificar cuando el mail y el teléfono son de dos contactos distintos de este store),
 * y un flag `conflictChecking` para deshabilitar el submit mientras el lookup está en vuelo.
 */
export const useContactConflictCheck = (form: FormInstance) => {
  const [conflictChecking, setConflictChecking] = useState(false);

  // Token to discard stale responses if the operator changes fields before
  // the previous lookup resolves.
  const lookupSeq = useRef(0);

  const runConflictCheck = async (
    whatsappRaw: string | undefined,
    emailRaw: string | undefined,
    storeCode: string | undefined,
  ): Promise<ContactConflictResult> => {
    const wa = normalizeWhatsapp(whatsappRaw);
    const em = normalizeEmail(emailRaw);

    const EMPTY: ContactConflictResult = {
      whatsappError: null,
      emailError: null,
      byWhatsapp: null,
      byEmail: null,
    };

    if (!wa && !em) return EMPTY;
    if (!storeCode) return EMPTY;

    const seq = ++lookupSeq.current;
    setConflictChecking(true);
    try {
      const res = await axiosInstance.get(`${API_URL_USERS}/lookup`, {
        params: {
          ...(wa ? { whatsapp: wa } : {}),
          ...(em ? { email: em } : {}),
          storeCode,
        },
      });

      // Stale response — newer lookup already started.
      if (seq !== lookupSeq.current) return EMPTY;

      const byWhatsapp = res.data?.byWhatsapp ?? null;
      const byEmail = res.data?.byEmail ?? null;

      let whatsappError: string | null = null;
      let emailError: string | null = null;

      /**
       * D8 — el único bloqueo que queda es **"ya existe acá"**.
       *
       * Que uno de los dos datos matchee y el otro no ya no es un conflicto: el alta procede, se
       * reusa la identidad y gana el dato que la persona ya tenía en el ecosistema. Los dos errores
       * que estaban acá —"Ya existe un usuario con ese teléfono" y "…con ese email y otro
       * teléfono"— dejaban al operador sin salida en un alta perfectamente válida, que es el caso
       * frecuente cuando se tipea de memoria el número de alguien que ya está cargado.
       *
       * El aviso de qué dato se cambió lo da el backend al guardar (`hydratedFields` /
       * `discardedFields`): sólo ahí se sabe cuál identidad se reusó y con qué datos quedó la ficha.
       */
      if (byWhatsapp?.isContactInStore) {
        whatsappError = "Ya existe un contacto en este store con ese teléfono";
      }

      if (byEmail?.isContactInStore) {
        emailError = "Ya existe un contacto en este store con ese email";
      }

      return { whatsappError, emailError, byWhatsapp, byEmail };
    } catch (err) {
      // Lookup failure shouldn't block the operator — backend's createUser
      // has the same defense-in-depth check.
      console.error("user lookup failed", err);
      return EMPTY;
    } finally {
      if (seq === lookupSeq.current) setConflictChecking(false);
    }
  };

  const applyConflictErrors = (
    whatsappError: string | null,
    emailError: string | null,
  ) => {
    form.setFields([
      { name: "whatsapp", errors: whatsappError ? [whatsappError] : [] },
      { name: "email", errors: emailError ? [emailError] : [] },
    ]);

    // setFields with empty errors clears manual errors but leaves the field
    // status undefined — antd's `hasFeedback` icon won't show the green check
    // until a rule-driven validation runs again. Re-trigger validation only on
    // fields we just cleared, so the success icon appears. Errors are
    // intentionally swallowed (rules may still legitimately reject other
    // fields; we don't want to mask their inline messages).
    const fieldsToRevalidate: string[] = [];
    if (!whatsappError) fieldsToRevalidate.push("whatsapp");
    if (!emailError) fieldsToRevalidate.push("email");
    if (fieldsToRevalidate.length) {
      form.validateFields(fieldsToRevalidate).catch(() => undefined);
    }
  };

  const handleConflictCheck = async (storeCode: string | undefined) => {
    const whatsapp = form.getFieldValue("whatsapp");
    const email = form.getFieldValue("email");
    const result = await runConflictCheck(whatsapp, email, storeCode);
    applyConflictErrors(result.whatsappError, result.emailError);
    return result;
  };

  /**
   * Helper to map the backend's structured BadRequestException response into
   * an inline form error. Returns true if the error was consumed (so callers
   * can suppress generic fallbacks).
   */
  const consumeBackendConflict = (error: any): boolean => {
    const data = error?.response?.data;
    const field = data?.field;
    const message: string | undefined = data?.message;
    if (message && (field === "whatsapp" || field === "email")) {
      form.setFields([{ name: field, errors: [message] }]);
      return true;
    }
    return false;
  };

  return {
    conflictChecking,
    runConflictCheck,
    applyConflictErrors,
    handleConflictCheck,
    consumeBackendConflict,
  };
};
